import * as React from "react";
import {DetailHeader} from "../../common/ui/detailsection/DetailHeader";
import {
  EntityProperty,
  FEATURE_TYPE_DETAILS_FIELD_UPDATE_STATUS_MESSAGES,
  PublishedWfsFeatureTypeModel
} from "./WfsPublishInfo";
import {Col} from "react-bootstrap";
import {FormattedMessage, InjectedIntl, InjectedIntlProps, injectIntl} from "react-intl";
import {Validation} from "./ValidatedTextBox";
import {Service} from "../model";
import {FeatureType, PublishedProduct} from "./model";
import {WithApi, WithApiProperties} from "../../common/util/WithApi";
import {selectors as serviceSelectors} from "../selectors";
import {actions} from "../actions";
import {connect} from "react-redux";
import {getErrorMessage} from "../../common/util/Util";

export type ReactElementWithValueChangeFunction<K> = React.ReactElement<{
  onValueChange: (entity: K, newValue: string) => Promise<Validation>
}>;

export interface DetailViewListField<T> {
  property: EntityProperty<T>
  name: any;
  value: ReactElementWithValueChangeFunction<PublishedWfsFeatureTypeModel>
  onValueChange?: (property: EntityProperty<T>,
                   updatedModelDataSelection: PublishedWfsFeatureTypeModel,
                   intl?: InjectedIntl) => Promise<Validation>;
}

interface WfsPublishInfoDetailsSectionProps {
  service: Service;
  servicePublishedProducts: PublishedProduct[];
  fields: DetailViewListField<FeatureType>[];
  data: PublishedWfsFeatureTypeModel;
  featureTypeNameChanged: (serviceId: string, featureTypeId: string,
                           publishedProduct: PublishedProduct) => Promise<void>;
  loadServicePublishedProducts: () => any;
}

type WfsPublishInfoDetailsSectionComponentProps =
    WfsPublishInfoDetailsSectionProps
    & WithApiProperties
    & InjectedIntlProps;

class WfsPublishInfoDetailsSectionComponent extends React.Component<WfsPublishInfoDetailsSectionComponentProps, {}> {

  constructor(props: WfsPublishInfoDetailsSectionComponentProps) {
    super(props);
  }

  createRowUI = ({property, name, onValueChange, value}: DetailViewListField<FeatureType>, intl: InjectedIntl) => {
    const onChange = async (entity: PublishedWfsFeatureTypeModel, newValue: string): Promise<Validation> => {
      const {servicePublishedProducts, service, featureTypeNameChanged, loadServicePublishedProducts} = this.props;
      const newEntity: PublishedWfsFeatureTypeModel = Object.assign({} as PublishedWfsFeatureTypeModel, entity);
      newEntity[property.key] = newValue;
      const publishedProductCopy = Object.assign({} as PublishedProduct, servicePublishedProducts.find(
          product => product.id === entity.productId));
      const featureType = publishedProductCopy.publishInfo.featureTypes.find(
          type => type.id === entity.featureTypeId
      );
      if (featureType) {
        if (property.key === 'xmlNamespacePrefix' && !newValue) {
          delete featureType[property.key];
        } else {
          featureType[property.key] = newValue;
        }
      }

      try {
        await featureTypeNameChanged(service.id, entity.featureTypeId, publishedProductCopy);
        return await onValueChange(property, newEntity, intl);
      } catch (error) {
        return Promise.resolve({
          state: "error",
          message: intl.formatMessage(FEATURE_TYPE_DETAILS_FIELD_UPDATE_STATUS_MESSAGES.failure,
              {field: property.name, errorMessage: getErrorMessage(error)})
        });
      } finally {
        // Reload the published products to ensure the UI is up-to-date
        await loadServicePublishedProducts();
      }
    }

    if (!value.props.onValueChange) {
      value = React.cloneElement(value, {onValueChange: onChange});
    } else {
      const originalOnValueChange = value.props.onValueChange;
      value.props.onValueChange = async (entity: PublishedWfsFeatureTypeModel,
                                         newValue: string): Promise<Validation> => {
        const validationResult: Validation = await onChange(entity, newValue);
        const originalValidationResult: Validation = await originalOnValueChange(entity, newValue);
        return {...validationResult, ...originalValidationResult};
      }
    }

    return (
        <div className="form-group detail-row" key={property.key}>
          <Col sm={3} className="detail-label">{name}</Col>
          <Col sm={10}>{value}</Col>
        </div>
    );
  }

  render() {
    const {fields, data, intl} = this.props;
    if (data && fields) {
      return (
          <div className="details" key={`details_div_${data.featureTypeId}`}>
            <DetailHeader title={<FormattedMessage id="studio.service.publishInfo.detail-section-component.title"
                                                   defaultMessage="Feature Type details"/>}/>
            <div className="detail-view" key={`detail-view_${data.featureTypeId}`}>
              {fields.map((field) => this.createRowUI(field, intl))}
            </div>
          </div>
      );
    }
  }
}

const mapStateToProps = (state, ownProps) => {
  const serviceId = ownProps.service.id; //injected by parent
  return {
    service: serviceSelectors.getServiceById(state, serviceId),
    servicePublishedProducts: serviceSelectors.getServicePublishedProducts(state, serviceId),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    featureTypeNameChanged: (serviceId: string, featureTypeId: string, publishedProduct: PublishedProduct) => dispatch(
        actions.updateServiceProductTypeName(serviceId, featureTypeId, publishedProduct))
  };
};

export const WfsPublishInfoDetailsSection = injectIntl(
    connect(mapStateToProps, mapDispatchToProps)(WithApi(WfsPublishInfoDetailsSectionComponent)));